import type { FixedIntervalCalendarProps as Props } from '../../typings'

import { Month } from '../../components/Month'
import { MonthNavigator } from '../../components/MonthNavigator'
import { useMonthNavigatorProps } from '../../hooks/useMonthNavigatorProps'
import { useMonths } from '../../hooks/useMonths'

import { Stack } from '@mui/material'
import { useFocusOnSelection } from './hooks/useFocusOnSelection'
import { useHoveredDay } from './hooks/useHoveredDay'
import { useIntervalDays } from './hooks/useIntervalDays'
import { useSelectionData } from './hooks/useSelectionData'
import { useUpdateSelection } from './hooks/useUpdateSelection'

export function FixedInterval(props: Props) {
  const {
    today,
    minDate,
    maxDate,
    onChange,
    dotColor,
    disabledDays,
    selectionEnd,
    referenceDate,
    intervalLength,
    highlightToday,
    selectionStart,
    highlightedDays,
    fixedRangeStart,
    tooltipIntlText,
    focusOnSelection,
    setReferenceDate,
    lastSelectableDate,
    firstNonArchivedDay,
    months: monthsCount,
    showNeighboringMonth,
    allowAutoMonthHeight,
  } = props

  // That hook that if the selection changes and is not already showed, the calendar scrolls to it
  useFocusOnSelection(referenceDate, setReferenceDate, selectionStart, focusOnSelection)

  const months = useMonths(referenceDate, showNeighboringMonth, monthsCount, minDate, maxDate)
  const [hoveredDay, onMouseOverDay] = useHoveredDay()

  const selectionData = useSelectionData(
    minDate,
    maxDate,
    intervalLength,
    selectionStart,
    selectionEnd,
    firstNonArchivedDay,
  )

  const daysByMonth = useIntervalDays({
    ...selectionData,
    firstNonArchivedDay,
    highlightedDays,
    tooltipIntlText,
    disabledDays,
    months,
    today,
    fixedRangeStart,
    selectionStart,
    highlightToday,
    intervalLength,
    lastSelectableDate,
    hoveredDay,
  })

  const monthNavigatorProps = useMonthNavigatorProps({
    referenceDate,
    setReferenceDate,
    months: monthsCount,
    minDateAsString: minDate,
    maxDateAsString: maxDate,
    labelFormat: 'month-year',
  })

  const handleOnClick = useUpdateSelection(onChange, lastSelectableDate, intervalLength)

  return (
    <Stack direction="column" height="auto" alignItems="center" flexShrink={0}>
      <MonthNavigator {...monthNavigatorProps} />
      <Stack direction="row" width="auto">
        {months.map(
          (month, index) =>
            daysByMonth[month.id] && (
              <Month
                index={index}
                key={month.id}
                showDaysOfWeek
                dotColor={dotColor}
                onClick={handleOnClick}
                days={daysByMonth[month.id]}
                onMouseOverDay={onMouseOverDay}
                allowAutoMonthHeight={allowAutoMonthHeight}
              />
            ),
        )}
      </Stack>
    </Stack>
  )
}
